import { Hours } from '@obraplan/core/domain/shared/hours';
import type {
  DailySchedule,
  DailyResourceRow,
} from '@obraplan/core/domain/planning/daily-schedule';

/**
 * Os impressos do **antes** — o que vai acontecer no dia.
 *
 * A programação e a conferência olham para o mesmo dia e para as mesmas pessoas, e é isso
 * que as fazia parecer a mesma coisa. O que as separa é o **tempo**: aqui o dia ainda não
 * aconteceu, e o papel serve para mandar gente trabalhar. Na conferência ele já aconteceu,
 * e o papel serve para registrar o que saiu.
 *
 * Por isso este arquivo não fala de executado nem de assinatura de conferência: fala de
 * ordem de serviço. A meta aparece porque ela é instrução — "faça 8 m² hoje" —, não
 * resultado.
 *
 * São dois documentos com o mesmo dado, para dois usos:
 *
 * - a **folha do dia** vai para o encarregado, com a equipe inteira;
 * - a **ficha individual** vai para a mão do funcionário, uma por página.
 */
export interface DailyScheduleReportProps {
  schedule: DailySchedule;
  dateLabel: string;
  projectName: string | undefined;
  /** Ausente = folha do dia inteiro. Presente = ficha daquele funcionário. */
  resourceId?: string;
}

export function DailyScheduleReport({
  schedule,
  dateLabel,
  projectName,
  resourceId,
}: DailyScheduleReportProps) {
  const selected =
    resourceId === undefined
      ? undefined
      : schedule.rows.find((row) => row.resource.resourceId === resourceId);

  return (
    <div className="hidden text-black print:block">
      {selected === undefined ? (
        <DaySheet schedule={schedule} dateLabel={dateLabel} projectName={projectName} />
      ) : (
        <IndividualSlip row={selected} dateLabel={dateLabel} projectName={projectName} />
      )}
    </div>
  );
}

/** `8h · 8,50 m²` — a instrução do dia, com a meta quando a frente é medida. */
function lineTargets(quarterHours: number, quantity: number | undefined, unit: string | undefined) {
  const hours = Hours.ofQuarters(quarterHours).format();
  if (quantity === undefined || unit === undefined) return hours;
  return `${hours} · ${String(quantity)} ${unit}`;
}

// ── A folha do dia ──────────────────────────────────────────────────────────────

function DaySheet({
  schedule,
  dateLabel,
  projectName,
}: {
  schedule: DailySchedule;
  dateLabel: string;
  projectName: string | undefined;
}) {
  const groups = groupByTeam(schedule.rows);

  return (
    <article>
      <header className="mb-4 border-b border-black pb-2">
        <h1 className="text-lg font-bold">Programação — {dateLabel}</h1>
        {projectName !== undefined && <p className="text-sm">Obra: {projectName}</p>}
        <p className="text-sm">
          {schedule.summary.workingResourceCount}{' '}
          {schedule.summary.workingResourceCount === 1 ? 'funcionário' : 'funcionários'} ·{' '}
          {Hours.ofQuarters(schedule.summary.plannedQuarterHours).format()} ·{' '}
          {schedule.summary.activityCount} frentes
        </p>
      </header>

      {schedule.rows.length === 0 ? (
        <p className="text-sm">Nada programado para esta data.</p>
      ) : (
        groups.map((group) => (
          <section key={group.team} className="mb-4">
            <h2 className="mb-1 text-xs font-bold uppercase">{group.team}</h2>

            {group.rows.map((row) => (
              <div
                key={row.resource.resourceId}
                className="mb-2 border-b border-dotted border-black pb-2"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-semibold">
                    {row.resource.name}
                    <span className="font-normal"> — {row.resource.detail}</span>
                  </p>
                  <p className="tabular text-sm font-semibold">
                    {Hours.ofQuarters(row.plannedQuarterHours).format()}
                  </p>
                </div>

                <ul className="pl-4">
                  {row.lines.map((line) => (
                    <li
                      key={line.allocation.id}
                      className="flex items-baseline justify-between gap-3 text-xs"
                    >
                      <span>
                        {line.activity.label} · {line.activity.towerName}
                      </span>
                      <span className="tabular">
                        {lineTargets(
                          line.quarterHours,
                          line.allocation.plannedQuantity,
                          line.activity.unitName,
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))
      )}

      {schedule.idle.length > 0 && (
        <section className="mt-4">
          <h2 className="mb-1 text-xs font-bold uppercase">Sem programação</h2>
          <p className="text-xs">{schedule.idle.map((resource) => resource.name).join(' · ')}</p>
        </section>
      )}

      <footer className="mt-6 border-t border-black pt-2 text-xs">
        <p>Encarregado: ______________________________ Assinatura: ______________________</p>
      </footer>
    </article>
  );
}

// ── A ficha individual ──────────────────────────────────────────────────────────

/**
 * O formato do requisito, para entregar na mão.
 *
 * ```text
 * 17/08/2026
 * Funcionário: João da Silva
 * Função: Pedreiro
 * Planejado: Alvenaria — 3º pavimento
 * Horas: 9h
 * ```
 */
function IndividualSlip({
  row,
  dateLabel,
  projectName,
}: {
  row: DailyResourceRow;
  dateLabel: string;
  projectName: string | undefined;
}) {
  return (
    <article>
      <header className="mb-4 border-b border-black pb-2">
        <h1 className="text-lg font-bold">{dateLabel}</h1>
        {projectName !== undefined && <p className="text-sm">Obra: {projectName}</p>}
      </header>

      <dl className="mb-4 text-sm">
        <div className="flex gap-2">
          <dt className="font-semibold">Funcionário:</dt>
          <dd>{row.resource.name}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-semibold">Função:</dt>
          <dd>{row.resource.detail}</dd>
        </div>
        {row.resource.teamName !== undefined && (
          <div className="flex gap-2">
            <dt className="font-semibold">Equipe:</dt>
            <dd>{row.resource.teamName}</dd>
          </div>
        )}
      </dl>

      <h2 className="mb-1 text-xs font-bold uppercase">Atividades do dia</h2>

      {row.lines.length === 0 ? (
        <p className="text-sm">Sem atividade programada para esta data.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black text-left">
              <th className="py-1 font-semibold">Atividade</th>
              <th className="py-1 font-semibold">Local</th>
              <th className="py-1 text-right font-semibold">Meta</th>
              <th className="py-1 text-right font-semibold">Horas</th>
            </tr>
          </thead>
          <tbody>
            {row.lines.map((line) => (
              <tr key={line.allocation.id} className="border-b border-dotted border-black">
                <td className="py-1">{line.activity.label}</td>
                <td className="py-1">{line.activity.towerName}</td>
                <td className="tabular py-1 text-right">
                  {line.allocation.plannedQuantity === undefined ||
                  line.activity.unitName === undefined
                    ? '—'
                    : `${String(line.allocation.plannedQuantity)} ${line.activity.unitName}`}
                </td>
                <td className="tabular py-1 text-right">
                  {Hours.ofQuarters(line.quarterHours).format()}
                </td>
              </tr>
            ))}
            <tr>
              <td className="py-1 font-semibold" colSpan={3}>
                Total
              </td>
              <td className="tabular py-1 text-right font-semibold">
                {Hours.ofQuarters(row.plannedQuarterHours).format()}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <footer className="mt-10 text-sm">
        <p>Assinatura: ______________________________________</p>
      </footer>
    </article>
  );
}

// ── Agrupamento ─────────────────────────────────────────────────────────────────

interface TeamGroup {
  readonly team: string;
  readonly rows: DailyResourceRow[];
}

/** Por equipe, na ordem que veio do domínio. Quem não tem equipe fecha a folha. */
function groupByTeam(rows: readonly DailyResourceRow[]): TeamGroup[] {
  const groups: TeamGroup[] = [];
  for (const row of rows) {
    const team = row.resource.teamName ?? 'Sem equipe';
    const last = groups[groups.length - 1];
    if (last !== undefined && last.team === team) last.rows.push(row);
    else groups.push({ team, rows: [row] });
  }
  return groups;
}
